/**
 * Karte „Tausch-Angebote" (Kapitel 6, Phase E) — zeigt auf der Profilseite
 * alle für den eingeloggten User relevanten Schichttausch-Vorgänge:
 *
 * - Eingehend, offen (an alle Kolleg:innen des Standorts): Annehmen möglich.
 * - Eingehend, gezielt an mich: Annehmen/Ablehnen möglich.
 * - Ausgehend, von mir erstellt: Zurückziehen möglich, solange `pending`.
 *
 * Bewusst nur `status === 'pending'` sichtbar — abgeschlossene Vorgänge
 * (angenommen/abgelehnt/storniert) verschwinden aus dieser Liste (Kapitel 6.3:
 * kein Verlauf nötig, die übernommene Schicht taucht ohnehin im Kalender auf).
 * Kein eigener Menüpunkt, kein Badge-Zähler hier — die Push-Benachrichtigung
 * (Entscheidung #42) ist der primäre Hinweisweg, diese Karte ist der
 * Nachschlage-Ort für den Fall, dass die Push verpasst wurde.
 */

import { useState } from 'react';
import { useAuth } from '../store/auth';
import { useShiftSwaps, useAcceptSwap, useRejectSwap, useCancelSwap } from '../data/shiftSwaps';
import { formatDate, formatTime } from '../lib/format';
import { hapticError, hapticSuccess } from '../lib/haptics';
import type { ApiShiftSwap } from '../lib/shiftSwaps';

export function SwapOffersCard() {
  const me = useAuth((s) => s.user);
  const { data: swaps } = useShiftSwaps();
  if (!me) return null;

  const pending = (swaps ?? []).filter((s) => s.status === 'pending');
  if (pending.length === 0) return null;

  const incoming = pending.filter((s) => s.fromUserId !== me.id);
  const outgoing = pending.filter((s) => s.fromUserId === me.id);

  return (
    <div className="card p-4 space-y-3">
      <p className="text-sm font-medium">Tausch-Angebote</p>
      <div className="space-y-2">
        {incoming.map((s) => (
          <IncomingRow key={s.id} swap={s} />
        ))}
        {outgoing.map((s) => (
          <OutgoingRow key={s.id} swap={s} />
        ))}
      </div>
    </div>
  );
}

function ShiftLine({ swap }: { swap: ApiShiftSwap }) {
  return (
    <p className="text-xs text-slate-400">
      {swap.shift.locationName} · {formatDate(swap.shift.shiftDate)}{' '}
      {formatTime(swap.shift.startTime)}–{formatTime(swap.shift.endTime)}
    </p>
  );
}

function IncomingRow({ swap }: { swap: ApiShiftSwap }) {
  const accept = useAcceptSwap();
  const reject = useRejectSwap();
  const [busy, setBusy] = useState<'accept' | 'reject' | null>(null);
  const open = swap.toUserId === null;

  async function onAccept() {
    setBusy('accept');
    try {
      await accept.mutateAsync(swap.id);
      hapticSuccess();
    } catch {
      hapticError();
    } finally {
      setBusy(null);
    }
  }

  async function onReject() {
    setBusy('reject');
    try {
      await reject.mutateAsync(swap.id);
      hapticSuccess();
    } catch {
      hapticError();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="bg-surface-2 rounded-lg p-3">
      <p className="text-sm flex items-center gap-2 flex-wrap">
        {open ? 'Offenes Tausch-Angebot' : 'Tausch-Angebot an dich'}
        {/* Screen 10 (Design-Canvas): Schicht an einem fremden Standort */}
        {swap.crossLocation && (
          <span className="badge bg-sky-500/20 text-sky-300 text-[10px]">standortübergreifend</span>
        )}
      </p>
      <ShiftLine swap={swap} />
      {swap.note && <p className="text-xs text-slate-400 mt-1">„{swap.note}"</p>}
      <div className="flex gap-2 mt-2">
        {!open && (
          <button
            onClick={onReject}
            disabled={busy !== null}
            className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-2 py-1 flex-1"
          >
            {busy === 'reject' ? '…' : 'Ablehnen'}
          </button>
        )}
        <button
          onClick={onAccept}
          disabled={busy !== null}
          className="text-xs bg-brand hover:bg-brand-dark rounded px-2 py-1 flex-1"
        >
          {busy === 'accept' ? '…' : 'Übernehmen'}
        </button>
      </div>
    </div>
  );
}

function OutgoingRow({ swap }: { swap: ApiShiftSwap }) {
  const cancel = useCancelSwap();
  const [busy, setBusy] = useState(false);

  async function onCancel() {
    setBusy(true);
    try {
      await cancel.mutateAsync(swap.id);
      hapticSuccess();
    } catch {
      hapticError();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-surface-2 rounded-lg p-3">
      <p className="text-sm flex items-center gap-2 flex-wrap">
        Dein Angebot {swap.toUserId ? 'an eine Kollegin/einen Kollegen' : '(offen)'}
        {!swap.toUserId && swap.openToOtherLocations && (
          <span className="badge bg-sky-500/20 text-sky-300 text-[10px]">auch andere Standorte</span>
        )}
      </p>
      <ShiftLine swap={swap} />
      {swap.note && <p className="text-xs text-slate-400 mt-1">„{swap.note}"</p>}
      <div className="flex gap-2 mt-2">
        <button
          onClick={onCancel}
          disabled={busy}
          className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-2 py-1 flex-1"
        >
          {busy ? '…' : 'Zurückziehen'}
        </button>
      </div>
    </div>
  );
}
